import { useTranslation } from "react-i18next"
import { Employment, MoneyInput, MonthsEnum, VariableCompensation } from "@oysterhr/schema"
import { buildMoney, formatMoney } from "./helpers"
import { useVariableCompensationSummary } from "./useVariableCompensationSummary"
import { PricingSummaryRow } from "../PricingSummaryRow/PricingSummaryRow"
import { Text } from "@oysterhr/ui"
import {
  fieldGuaranteedOptions,
  supportsGuaranteedField,
} from "~/app/utils/variableCompensationHelpers"
import { useAccountAspect } from "@oysterhr/identity/AccountAspectContext"
import { SalaryPeriodEnumType } from "@oysterhr/hiring/full-time-employee"

type VariableCompensationCommonProps = {
  displayProratedSummary: boolean
  frequency: VariableCompensation["paymentFrequency"]
  startDate?: Employment["startDate"]
  effectiveDate?: VariableCompensation["effectiveDate"]
  prorated: boolean | undefined | null
  selectedMonths?: MonthsEnum[]
  // Fields only needed for percentage var. comp.
  salaryPeriod?: SalaryPeriodEnumType
  employmentId?: Employment["id"]
} & Pick<
  VariableCompensation,
  "compensationType" | "statutory" | "includedInGrossSalary" | "guaranteed"
>

export type VariableCompensationSummaryPercentageProps = VariableCompensationCommonProps & {
  type: "percentage"
  salary: MoneyInput
  percentage: number
}

export type VariableCompensationSummaryAmountProps = VariableCompensationCommonProps & {
  type: "amount"
  amount?: MoneyInput
  templateAmount?: MoneyInput
}

export type VariableCompensationSummaryProps =
  | VariableCompensationSummaryPercentageProps
  | VariableCompensationSummaryAmountProps

export function VariableCompensationSummary(props: VariableCompensationSummaryProps) {
  const { t } = useTranslation("variable_compensation/Common")
  const { isStaff } = useAccountAspect()
  const isPercentage = props.type === "percentage"
  const isHourlySalary = props.salaryPeriod === SalaryPeriodEnumType.Hourly

  const { titles, values, templateAmount, yearlySalaryMoney } =
    useVariableCompensationSummary(props)

  return (
    <div data-testid="variable-compensation-summary">
      {isPercentage && (
        <PricingSummaryRow
          title={
            isHourlySalary
              ? t("GENERIC.summary.annualSalaryPrediction")
              : t("GENERIC.summary.annualSalary")
          }
          titleValue={formatMoney(yearlySalaryMoney || buildMoney(props.salary))}
        />
      )}
      <PricingSummaryRow
        title={titles.periodic}
        titleValue={values.periodic}
        subtitle={titles.periodicDescription}
        subtitleValue={values.periodicDescription}
      />

      {templateAmount && (
        <Text size="small" className="mb-4 text-gray-400">
          {t("GENERIC.summary.fromTemplate", {
            templateAmount,
          })}
        </Text>
      )}

      {isStaff && (
        <div className="mb-2">
          {supportsGuaranteedField(props.compensationType) && (
            <div className="flex justify-between">
              <Text size="small" as="span">
                {t("GENERIC.guaranteed.label")}
              </Text>
              <Text size="small" as="span">
                {
                  fieldGuaranteedOptions({ hasNullOption: true })?.find(
                    ({ value }) => value === props.guaranteed,
                  )?.label
                }
              </Text>
            </div>
          )}
          <div className="flex justify-between">
            <Text size="small" as="span">
              {t("GENERIC.statutory")}
            </Text>
            <Text size="small" as="span">
              {props.statutory ? t("GENERIC.summary.yes") : t("GENERIC.summary.no")}
            </Text>
          </div>
          <div className="flex justify-between">
            <Text size="small" as="span">
              {t("GENERIC.includedInGrossSalary")}
            </Text>
            <Text size="small" as="span">
              {props.includedInGrossSalary ? t("GENERIC.summary.yes") : t("GENERIC.summary.no")}
            </Text>
          </div>
        </div>
      )}

      {props.displayProratedSummary && props.prorated != null && (
        <div className="mb-4 mt-2 flex w-full justify-between text-black">
          <Text size="small" as="span">
            {t("GENERIC.summary.paymentsProrated")}
          </Text>
          <Text size="small" as="span" className="whitespace-nowrap">
            {props.prorated ? t("GENERIC.summary.yes") : t("GENERIC.summary.no")}
          </Text>
        </div>
      )}

      <PricingSummaryRow
        title={titles.annual}
        titleValue={values.annual}
        subtitle={titles.annualDescription}
        subtitleValue={values.annualDescription}
        highlight
      />
    </div>
  )
}
